/** @format */

import React, { useEffect, useState } from "react";
import NotificationsPanel from "./NotificationsPanel";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/Logo.svg";
import { useLogout } from "@/services/python-api/authHooks.js";
import SidebarCollapseIcon from "@/components/sidebar/SidebarCollapseIcon";
import SidebarCollapsedLogo from "@/components/sidebar/SidebarCollapsedLogo";
import { useMyAccount } from "@/services/python-api/customerHooks";
import {
  AlignHorizontalDistributeCenter,
  Bell,
  BadgeCheck,
  Car,
  FileText,
  Home,
  Receipt,
  Map,
  MessageCircleMore,
  LogOut,
  MoreHorizontal,
  Package,
  ShieldCheck,
  Ship,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";

const ICON_SIZE = 20;
const ICON_COLOR = "#1D2739";

const Sidebar = ({
  isCollapsed,
  isMobile = false,
  isOpen,
  onToggleCollapse,
  onExpand,
  onClose,
  userName = null,
}) => {
  const [notifOpen, setNotifOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const LogUserOut = useLogout();

  const handleLogout = () => {
    LogUserOut.mutate();
  };

  // A customer's own view (29 Oct): only for a login an admin linked to a customer.
  const { data: account } = useMyAccount();
  const waiting = account?.decisions_waiting ?? 0;
  const customerItems = account
    ? [
        { text: "Your cars", icon: () => <Car size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-cars" },
        ...(account.containers
          ? [{ text: "Containers", icon: () => <Package size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-containers" }]
          : []),
        ...(account.join && !account.partner
          ? [{ text: "Next containers", icon: () => <Ship size={ICON_SIZE} color={ICON_COLOR} />, path: "/next-containers" }]
          : []),
        ...(account.join && account.partner
          ? [{ text: "Offer a container", icon: () => <Ship size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-offers" }]
          : []),
        ...(account.partner
          ? []
          : [
              {
                text: waiting ? `Decisions (${waiting})` : "Decisions",
                icon: () => <BadgeCheck size={ICON_SIZE} color={waiting ? "#2563EB" : ICON_COLOR} />,
                path: "/my-decisions",
              },
              { text: "Invoices", icon: () => <Receipt size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-invoices" },
            ]),
        { text: "Papers", icon: () => <FileText size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-papers" },
        ...(account.join && !account.partner
          ? [{ text: "Purchase details", icon: () => <ShieldCheck size={ICON_SIZE} color={ICON_COLOR} />, path: "/purchase-details" }]
          : []),
        ...(account.team
          ? [{ text: "Ask Dockie", icon: () => <Sparkles size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-dockie" }]
          : []),
        ...(account.team && !account.partner
          ? [{ text: "Team", icon: () => <Users size={ICON_SIZE} color={ICON_COLOR} />, path: "/my-team" }]
          : []),
      ]
    : [];

  const allItems = [
    {
      text: "Home",
      icon: () => <Home size={ICON_SIZE} color={ICON_COLOR} />,
      path: "/home",
    },
    ...customerItems,
    {
      text: "Chats",
      icon: () => <MessageCircleMore size={ICON_SIZE} color={ICON_COLOR} />,
      path: "/chats/new",
    },
    {
      text: "Shipments",
      icon: (
        <svg
          width={ICON_SIZE}
          height={ICON_SIZE}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.1666 17.5C13.2462 17.5 12.5 16.7538 12.5 15.8334V15H7.49998V15.8334C7.49998 16.7538 6.75379 17.5 5.83331 17.5H3.74998C2.8295 17.5 2.08331 16.7538 2.08331 15.8334V14.8267C1.34752 14.5052 0.833313 13.771 0.833313 12.9167V10C0.833313 9.62785 0.955316 9.28415 1.16151 9.00675L1.07739 8.92263C0.751954 8.59719 0.751954 8.06956 1.07739 7.74412C1.36364 7.45787 1.80631 7.4234 2.13024 7.6407L2.9962 5.0428C3.33649 4.02195 4.29184 3.33337 5.36791 3.33337H14.632C15.7081 3.33337 16.6635 4.02195 17.0038 5.0428L17.8697 7.6407C18.1936 7.4234 18.6363 7.45787 18.9226 7.74412C19.248 8.06956 19.248 8.59719 18.9226 8.92263L18.8384 9.00675C19.0446 9.28415 19.1666 9.62785 19.1666 10V12.9167C19.1666 13.771 18.6524 14.5052 17.9166 14.8267V15.8334C17.9166 16.7538 17.1705 17.5 16.25 17.5H14.1666ZM4.57734 5.56985C4.69077 5.22957 5.00922 5.00004 5.36791 5.00004H14.632C14.9907 5.00004 15.3092 5.22957 15.4226 5.56985L16.3438 8.33337H3.65617L4.57734 5.56985ZM2.91665 13.3334C2.68653 13.3334 2.49998 13.1468 2.49998 12.9167V10H17.5V12.9167C17.5 13.1468 17.3134 13.3334 17.0833 13.3334H2.91665ZM14.1666 15H16.25V15.8334H14.1666V15ZM3.74998 15H5.83331V15.8334H3.74998V15Z"
            fill={ICON_COLOR}
          />
        </svg>
      ),
      path: "/shipments",
    },
    {
      text: "Tracking",
      icon: () => <Map size={ICON_SIZE} color={ICON_COLOR} />,
      path: "/tracking",
    },
    {
      text: "Documents",
      icon: () => <FileText size={ICON_SIZE} color={ICON_COLOR} />,
      path: "/documents",
    },
    {
      text: "Analytics",
      icon: () => <AlignHorizontalDistributeCenter size={ICON_SIZE} color={ICON_COLOR} />,
      path: "/analytics",
    },
  ];

  // With a customer's own view, its six screens are the menu; the rest fold under More,
  // so each screen keeps one job (Claret, 30 Oct).
  const [showMore, setShowMore] = useState(false);
  const menuItems = account
    ? [
        ...customerItems,
        {
          text: showMore ? "Less" : "More",
          icon: () => <MoreHorizontal size={ICON_SIZE} color={ICON_COLOR} />,
          path: "#more",
          onClick: () => setShowMore((open) => !open),
        },
        ...(showMore ? allItems.filter((item) => !customerItems.includes(item)) : []),
      ]
    : allItems;

  const bottomMenuItems = [
    {
      text: "Notifications",
      icon: <Bell size={ICON_SIZE} color={ICON_COLOR} />,
      onClick: () => setNotifOpen(true),
      dataTour: "notifications",
    },
    {
      text: "Settings",
      icon: <Settings size={ICON_SIZE} color={ICON_COLOR} />,
      onClick: () => navigate("/settings"),
      dataTour: "settings",
    },
    {
      text: "Logout",
      icon: LogUserOut.isPending ? null : <LogOut size={ICON_SIZE} color={ICON_COLOR} />,
      onClick: handleLogout,
      isLoading: LogUserOut.isPending,
      loadingText: "Logging out...",
    },
  ];

  useEffect(() => {
    const existingToken = localStorage.getItem("dockporter_token");
    if (!existingToken) navigate("/auth/login");
  }, [navigate]);

  const renderNavIcon = (item, isActive) => {
    const icon = typeof item.icon === "function" ? item.icon(isActive) : item.icon;
    return <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{icon}</div>;
  };

  const renderSidebarContent = () => (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="dockie-sidebar-scroll min-h-0 flex-1 overflow-y-auto px-3 pt-3">
        <div className="flex flex-col w-full">
          {menuItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== "/home" && location.pathname.startsWith(item.path));
            return (
              <div
                key={item.text}
                data-tour={item.text.toLowerCase().replace(" ", "-")}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick ? item.onClick() : navigate(item.path);
                    if (isMobile) onClose();
                  }
                }}
                className={`mb-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors ${
                  item.disabled
                    ? "cursor-not-allowed opacity-50"
                    : isActive
                      ? "cursor-pointer bg-[#EFF6FF]"
                      : "cursor-pointer hover:bg-[#F5F5F5]"
                }`}
              >
                {renderNavIcon(item, isActive)}
                <span className="text-[14px] font-normal text-black">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-auto flex flex-shrink-0 flex-col gap-1 px-3 pb-4">
        {bottomMenuItems.map((item) => (
          <div
            key={item.text}
            data-tour={item.dataTour}
            onClick={item.onClick}
            className="mb-0.5 flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[14px] font-normal text-black transition-colors hover:bg-[#F5F5F5]"
          >
            {item.isLoading ? (
              <span className="text-[14px] text-black">{item.loadingText}</span>
            ) : (
              <>
                <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{item.icon}</div>
                <span>{item.text}</span>
              </>
            )}
          </div>
        ))}

        <div className="mt-4 flex w-full items-center gap-2 px-1">
          <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-[#E3EFFC] text-[14px] font-semibold text-black">
            {userName ? userName?.firstName?.charAt(0).toUpperCase() : "?"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-medium leading-tight text-black">
              {`${userName?.firstName ?? ""} ${userName?.lastName ?? ""}`.trim() ||
                userName?.username ||
                "User"}
            </p>
            <p className="mt-0.5 truncate text-[10px] font-normal leading-tight text-gray-500">
              {userName?.email || "user@email.com"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCollapsedNavItem = (item) => {
    const isActive =
      location.pathname === item.path ||
      (item.path !== "/home" && location.pathname.startsWith(item.path));
    return (
      <div
        key={item.text}
        title={item.text}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!item.disabled) item.onClick ? item.onClick() : navigate(item.path);
        }}
        className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
          item.disabled
            ? "cursor-not-allowed opacity-50"
            : isActive
              ? "cursor-pointer bg-[#EFF6FF]"
              : "cursor-pointer hover:bg-[#F5F5F5]"
        }`}
      >
        {renderNavIcon(item, isActive)}
      </div>
    );
  };

  if (isCollapsed && !isMobile) {
    return (
      <>
        <div className="flex h-screen w-16 shrink-0 flex-col border-r border-[#E4E7EC] bg-white">
          <div className="flex h-[52px] flex-shrink-0 items-center justify-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
              aria-label="Expand sidebar"
              className="group relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-[#F5F5F5]"
            >
              <span className="transition-opacity duration-200 group-hover:opacity-0">
                <SidebarCollapsedLogo />
              </span>
              <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <SidebarCollapseIcon expanded size="sm" />
              </span>
            </button>
          </div>

        <div className="dockie-sidebar-scroll flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto px-2">
          {menuItems.map(renderCollapsedNavItem)}
        </div>

        <div className="mt-auto flex flex-shrink-0 flex-col items-center gap-2 px-2 pb-4">
          {bottomMenuItems.map((item) => (
            <div
              key={item.text}
              title={item.text}
              data-tour={item.dataTour}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-[#F5F5F5]"
            >
              {item.isLoading ? (
                <span className="text-[10px]">...</span>
              ) : (
                <div className="flex h-5 w-5 items-center justify-center">{item.icon}</div>
              )}
            </div>
          ))}
          <div
            title={userName?.firstName || "User"}
            className="mt-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#E3EFFC] text-[12px] font-semibold text-[#101928]"
          >
            {userName ? userName?.firstName?.charAt(0).toUpperCase() : "?"}
          </div>
        </div>
        </div>
        {notifOpen && <NotificationsPanel onClose={() => setNotifOpen(false)} />}
      </>
    );
  }

  if (isMobile) {
    return (
      <>
        <div
          className="fixed top-0 left-0 z-[1300] flex h-screen w-[85vw] max-w-80 flex-col border-r border-gray-100 bg-white shadow-xl transition-transform duration-300 ease-in-out"
          style={{
            transform: isOpen && !notifOpen ? "translateX(0)" : "translateX(-100%)",
            boxShadow: isOpen && !notifOpen ? "2px 0 10px rgba(0,0,0,0.1)" : "none",
            visibility: isOpen && !notifOpen ? "visible" : "hidden",
          }}
        >
          <div className="box-border flex h-[52px] flex-shrink-0 items-center justify-between px-5">
            <img src={Logo} alt="Dockie" className="h-[20.91px] w-[66px] object-contain" />
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-[#F5F5F5] hover:text-gray-600"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="dockie-sidebar-scroll min-h-0 flex-1 overflow-y-auto px-5 pt-3">
              <div className="flex flex-col w-full">
                {menuItems.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== "/home" && location.pathname.startsWith(item.path));
                  return (
                    <div
                      key={item.text}
                      onClick={() => {
                        if (!item.disabled) {
                          item.onClick ? item.onClick() : navigate(item.path);
                          onClose();
                        }
                      }}
                      className={`mb-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors ${
                        item.disabled
                          ? "cursor-not-allowed opacity-50"
                          : isActive
                            ? "cursor-pointer bg-[#EFF6FF]"
                            : "cursor-pointer hover:bg-[#F5F5F5]"
                      }`}
                    >
                      {renderNavIcon(item, isActive)}
                      <span className="text-[14px] font-normal text-black">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-auto flex flex-shrink-0 flex-col gap-1 px-5 pb-6">
              {bottomMenuItems.map((item) => (
                <div
                  key={item.text}
                  data-tour={item.dataTour}
                  onClick={() => {
                    item.onClick();
                    if (item.text !== "Notifications") onClose();
                  }}
                  className="mb-0.5 flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[14px] font-normal text-black transition-colors hover:bg-[#F5F5F5]"
                >
                  {item.isLoading ? (
                    <span className="text-[14px] text-black">{item.loadingText}</span>
                  ) : (
                    <>
                      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{item.icon}</div>
                      <span>{item.text}</span>
                    </>
                  )}
                </div>
              ))}

              <div className="mt-4 flex w-full items-center gap-2 px-1">
                <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-[#E3EFFC] text-[14px] font-semibold text-black">
                  {userName ? userName?.firstName?.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-medium leading-tight text-black">
                    {`${userName?.firstName ?? ""} ${userName?.lastName ?? ""}`.trim() ||
                      userName?.username ||
                      "User"}
                  </p>
                  <p className="mt-0.5 truncate text-[10px] font-normal leading-tight text-gray-500">
                    {userName?.email || "user@email.com"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {notifOpen && <NotificationsPanel onClose={() => setNotifOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <div className="flex h-screen w-[210px] shrink-0 flex-col border-r border-[#E4E7EC] bg-white">
        <div className="box-border flex h-[52px] flex-shrink-0 items-center justify-between px-5">
          <img src={Logo} alt="DockPorter" className="h-[20.91px] w-[66px] object-contain" />
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label="Collapse sidebar"
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-[#F5F5F5]"
          >
            <SidebarCollapseIcon size="sm" />
          </button>
        </div>
        {renderSidebarContent()}
      </div>
      {notifOpen && <NotificationsPanel onClose={() => setNotifOpen(false)} />}
    </>
  );
};

export default Sidebar;
